import type { AttachmentInput } from "@amzn/ttechclaimintakeservice-client";
import type { CloudscapeJsonSchema } from "@amzn/fin-tech-cloudscape-json-forms";

import { LineOfBusiness } from "src/app/types";
import { buildAttachments } from "src/forms/FileUpload/FileUploadUtilities";
import type { SerializedFile } from "src/forms/FileUpload/types";

/**
 * Schema extension that routes a property to `S3FileUploadControl` instead of
 * the library's built-in file control.
 *
 * The built-in control base64-encodes files into the form data as `data-url`
 * strings. Claim attachments go to S3 instead, and the stored value is an
 * object per file rather than a string, so the two cannot share a trigger: a
 * schema written for one fails validation under the other. Hence an app-owned
 * key rather than reusing `x-file-upload`.
 *
 * A property using it MUST be `type: "array"`, since the control always stores
 * a list even when only one file is allowed.
 */
export const S3_UPLOAD_EXTENSION = "x-s3-upload";

/**
 * Maps the `lineOfBusiness` value carried on `ResolveSchemaInput` to the
 * `LineOfBusiness` enum that keys the upload bucket and the file size caps.
 *
 * The two vocabularies differ because `ResolveSchemaInput.lineOfBusiness` is
 * part of the service contract while `LineOfBusiness` is internal to the UI.
 */
export const LINE_OF_BUSINESS_BY_RESOLVE_INPUT: { [key: string]: LineOfBusiness } = {
  "workers-compensation": LineOfBusiness.WORKERS_COMPENSATION,
  cargo: LineOfBusiness.CARGO,
  "product-liability": LineOfBusiness.IPL,
  atoz: LineOfBusiness.ATOZ,
};

/** Names of the properties this schema models as S3 attachment fields. */
export function attachmentFieldNames(schema: CloudscapeJsonSchema): string[] {
  const properties = schema.properties ?? {};
  return Object.keys(properties).filter((name) => properties[name]?.[S3_UPLOAD_EXTENSION] === true);
}

/**
 * Converts each attachment field from the metadata the form holds into the
 * `AttachmentInput` shape `createClaim` expects.
 *
 * The form stores a `SerializedFile` per attachment so the upload control can
 * redraw its tokens after a reload, but the service only wants the S3
 * coordinates — it reads the bytes out of the bucket the control already PUT
 * them to. Sending the form's own shape would hand the service fields it does
 * not model (`lastModified`, `webkitRelativePath`) while withholding the ones
 * it does (`fileName`, `fileExtension`).
 *
 * Fields with no attachments are omitted rather than sent as empty arrays,
 * matching how the description companions are built.
 */
export function buildAttachmentFields(
  schema: CloudscapeJsonSchema,
  data: Record<string, unknown>
): Record<string, AttachmentInput[]> {
  const fields: Record<string, AttachmentInput[]> = {};
  for (const name of attachmentFieldNames(schema)) {
    const stored = data[name];
    if (Array.isArray(stored) && stored.length > 0) {
      fields[name] = buildAttachments(stored as SerializedFile[]);
    }
  }
  return fields;
}
